/**
 * Utilities for validating analytics events.
 */

import type { AnalyticsEvent } from '../types/events.js';
import type { EventCategory } from '../constants/event-categories.js';
import type { EventProperties } from '../types/properties.js';
import { EVENT_CATEGORIES } from '../constants/event-categories.js';
import { EVENT_NAMES } from '../constants/event-names.js';

/**
 * Payload `track()` accepts before name/category checks. Name is a
 * string so a leaked illegal name can reach this function instead of
 * being dropped by a caller-side allowlist.
 */
export type UnvalidatedAnalyticsEvent = {
  name?: string;
  category?: EventCategory | string;
  properties?: EventProperties;
  timestamp?: number;
};

/**
 * Validate an analytics event.
 *
 * This function checks that the event has a valid name and category.
 *
 * @param event The event to validate
 * @returns The validated event
 * @throws Error if the event is invalid
 */
export function validateEvent(event: UnvalidatedAnalyticsEvent): AnalyticsEvent {
  // Check that the event has a name
  if (!event.name) {
    throw new Error('Event must have a name');
  }

  // Check that the event has a valid name
  const validNames: readonly string[] = Object.values(EVENT_NAMES);
  if (!validNames.includes(event.name)) {
    throw new Error(`Invalid event name: ${event.name}`);
  }

  // Check that the event has a category
  if (!event.category) {
    throw new Error('Event must have a category');
  }

  // Check that the event has a valid category
  const validCategories: readonly string[] = Object.values(EVENT_CATEGORIES);
  if (!validCategories.includes(event.category)) {
    throw new Error(`Invalid event category: ${event.category}`);
  }

  // includes() proved name/category are catalog members. Reconstruct so
  // optional keys stay absent (not `undefined`) under exactOptionalPropertyTypes;
  // tsc cannot see that proof, so the return is asserted.
  return {
    name: event.name,
    category: event.category,
    ...(event.properties !== undefined ? { properties: event.properties } : {}),
    ...(event.timestamp !== undefined ? { timestamp: event.timestamp } : {})
  } as AnalyticsEvent;
}
